import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { supabase } from '../../lib/supabase'
import { loadDueDateConfigs, type DueDateConfigRow, type DueDateConfigSource } from '../../lib/dueDateConfig'
import { DEFAULT_DUE_DATE_GROUPS, normalizeGroupCode, validateDueDateGroups, type DueDateGroupMap } from '../../lib/dueDateGroups'
import '../operational/VencimentoGrupo.css'

type DraftRelease = { id: number; version: string; titulo: string; status: 'rascunho' }
type DraftChange = { id: number; record_key: string; approval_status: string }
type GroupRow = { id: string; group: string; daysText: string }

function nextPatchVersion(version: string) {
  const parts = version.split('.').map((part) => Number.parseInt(part, 10))
  if (parts.length >= 3 && parts.every(Number.isFinite)) return `${parts[0]}.${parts[1]}.${parts[2] + 1}`
  return '8.0.1'
}

function dateToday() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

function payloadRows(groups: DueDateGroupMap): GroupRow[] {
  return Object.entries(groups)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([group, days], index) => ({ id: `${group}-${index}`, group, daysText: days.join(', ') }))
}

function configData(row: DueDateConfigRow) {
  return { config_key: row.config_key, effective_from: row.effective_from, title: row.title, payload: row.payload }
}

function sourceText(source: DueDateConfigSource) {
  if (source === 'online') return 'Publicado no Supabase'
  if (source === 'cache') return 'Contingência em cache — edição temporariamente indisponível'
  return 'Regra padrão integrada — banco ainda sem esta configuração; edição bloqueada até a migration'
}

export function VencimentoGrupoAdmin({ onBack }: { onBack: () => void }) {
  const { profile } = useAuth()
  const [configs, setConfigs] = useState<DueDateConfigRow[]>([])
  const [source, setSource] = useState<DueDateConfigSource>('fallback')
  const [draftRelease, setDraftRelease] = useState<DraftRelease | null>(null)
  const [drafts, setDrafts] = useState<DraftChange[]>([])
  const [editingId, setEditingId] = useState<number | null>(null)
  const [effectiveFrom, setEffectiveFrom] = useState(dateToday())
  const [title, setTitle] = useState('Vencimentos por Grupo de Leitura')
  const [groupRows, setGroupRows] = useState<GroupRow[]>(payloadRows(DEFAULT_DUE_DATE_GROUPS))
  const [formOpen, setFormOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const canEdit = source === 'online'

  async function load() {
    setLoading(true); setError('')
    try {
      const [loaded, releaseResult] = await Promise.all([
        loadDueDateConfigs(),
        supabase.from('releases').select('id,version,titulo,status').eq('status', 'rascunho').order('id', { ascending: false }).limit(1).maybeSingle(),
      ])
      setConfigs([...loaded.rows].sort((a, b) => b.effective_from.localeCompare(a.effective_from)))
      setSource(loaded.source)
      if (releaseResult.error) throw releaseResult.error
      const release = releaseResult.data as DraftRelease | null
      setDraftRelease(release)
      if (release && loaded.source === 'online') {
        const draftResult = await supabase.from('draft_changes').select('id,record_key,approval_status').eq('release_id', release.id).eq('dataset', 'config_vencimentos_grupo').order('id')
        if (draftResult.error) throw draftResult.error
        setDrafts((draftResult.data ?? []) as DraftChange[])
      } else setDrafts([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar as regras de vencimento.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  const currentConfig = useMemo(() => {
    const today = dateToday()
    return [...configs].filter((row) => row.effective_from <= today).sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] ?? configs[0] ?? null
  }, [configs])

  async function ensureDraftRelease() {
    if (!canEdit) throw new Error('A edição só é liberada quando a configuração estiver instalada e online no Supabase.')
    if (draftRelease) return draftRelease
    if (!profile) throw new Error('Perfil não carregado.')
    const latest = await supabase.from('releases').select('version').order('id', { ascending: false }).limit(1).maybeSingle()
    if (latest.error) throw latest.error
    const version = nextPatchVersion(latest.data?.version ?? '8.0.0')
    const created = await supabase.from('releases').insert({ version, titulo: `Atualização Suite ${version}`, resumo: 'Alterações preparadas pelo painel administrativo.', status: 'rascunho', created_by: profile.id }).select('id,version,titulo,status').single()
    if (created.error) throw created.error
    const release = created.data as DraftRelease
    setDraftRelease(release)
    return release
  }

  function openConfig(config?: DueDateConfigRow) {
    if (!canEdit) {
      setError('Edição bloqueada neste ambiente até a migration de Vencimentos por Grupo ser instalada no Supabase.')
      return
    }
    const row = config ?? currentConfig
    setEditingId(row && row.id > 0 ? row.id : null)
    setEffectiveFrom(row?.effective_from ?? dateToday())
    setTitle(row?.title ?? 'Vencimentos por Grupo de Leitura')
    setGroupRows(payloadRows(row?.payload.groups ?? DEFAULT_DUE_DATE_GROUPS))
    setFormOpen(true); setError(''); setMessage('')
  }

  function addGroup() {
    setGroupRows((rows) => [...rows, { id: `new-${Date.now()}`, group: '', daysText: '' }])
  }

  function updateRow(id: string, patch: Partial<GroupRow>) {
    setGroupRows((rows) => rows.map((row) => row.id === id ? { ...row, ...patch } : row))
  }

  function removeRow(id: string) {
    setGroupRows((rows) => rows.filter((row) => row.id !== id))
  }

  function buildGroups() {
    const groups: DueDateGroupMap = {}
    for (const row of groupRows) {
      const group = normalizeGroupCode(row.group)
      if (!group) throw new Error(`Grupo inválido: ${row.group || 'em branco'}.`)
      if (groups[group]) throw new Error(`O grupo ${group} foi informado mais de uma vez.`)

      const parts = row.daysText.split(/[;,\s]+/).filter(Boolean)
      if (!parts.length) throw new Error(`Informe ao menos um dia permitido para o grupo ${group}.`)
      const rawDays = parts.map((part) => Number(part))
      if (rawDays.some((day) => !Number.isInteger(day) || day < 1 || day > 31)) throw new Error(`O grupo ${group} possui dia inválido. Use somente números inteiros de 1 a 31.`)
      if (new Set(rawDays).size !== rawDays.length) throw new Error(`O grupo ${group} possui dias duplicados.`)
      groups[group] = [...rawDays].sort((a, b) => a - b)
    }
    const errors = validateDueDateGroups(groups)
    if (errors.length) throw new Error(errors[0])
    return groups
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    if (!profile) return
    if (!canEdit) return setError('Edição indisponível enquanto a configuração não estiver online no Supabase.')
    if (!effectiveFrom || !title.trim()) return setError('Informe vigência e identificação da regra.')
    setSaving(true); setError(''); setMessage('')
    try {
      const groups = buildGroups()
      const release = await ensureDraftRelease()
      const existing = editingId == null ? null : configs.find((row) => row.id === editingId) ?? null
      if (!existing && configs.some((row) => row.id > 0 && row.effective_from === effectiveFrom)) throw new Error('Já existe uma regra publicada para esta vigência. Edite a existente ou informe nova data.')
      const data = { config_key: 'vencimentos_grupo', effective_from: effectiveFrom, title: title.trim(), payload: { holder_only: true, groups } }
      const recordKey = existing ? `id:${existing.id}` : `new:config_vencimentos_grupo:${effectiveFrom}`
      const result = await supabase.from('draft_changes').upsert({
        release_id: release.id,
        dataset: 'config_vencimentos_grupo',
        operacao: existing ? 'UPDATE' : 'INSERT',
        record_key: recordKey,
        dados_anteriores: existing ? configData(existing) : null,
        dados_novos: data,
        created_by: profile.id,
        updated_by: profile.id,
      }, { onConflict: 'release_id,dataset,record_key' })
      if (result.error) throw result.error
      setMessage(`Regra salva no rascunho ${release.version}. A configuração publicada ainda não foi alterada.`)
      setFormOpen(false)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar a regra.')
    } finally {
      setSaving(false)
    }
  }

  const draftForCurrent = currentConfig?.id ? drafts.find((item) => item.record_key === `id:${currentConfig.id}`) : null

  return (
    <section className="workspace-content admin-editor operational-config-editor due-date-admin">
      <div className="page-heading">
        <div>
          <button className="text-button" onClick={onBack}>← Voltar ao painel</button>
          <p className="eyebrow">CONFIGURAÇÕES OPERACIONAIS</p>
          <h1>Vencimentos por Grupo</h1>
          <p className="muted">Gerencie os Grupos de Leitura e os dias permitidos. Alterações seguem rascunho, aprovação e publicação global.</p>
        </div>
        <div className="heading-actions">
          {draftRelease && canEdit && <span className="status-chip warning">Rascunho {draftRelease.version}</span>}
          <button className="primary-button compact" onClick={() => openConfig()} disabled={!canEdit}>{canEdit ? 'Editar regra vigente' : 'Aguardando instalação'}</button>
        </div>
      </div>

      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}
      <div className={`notice ${source === 'fallback' ? 'info' : source === 'cache' ? 'warning' : 'success'}`}>{sourceText(source)}.</div>

      {loading && <div className="empty-state">Carregando regras...</div>}

      {!loading && currentConfig && !formOpen && (
        <>
          <section className="editor-form-card">
            <div className="due-date-admin-head">
              <div><p className="eyebrow">REGRA VIGENTE</p><h2>{currentConfig.title}</h2><p className="muted">Vigência {new Date(`${currentConfig.effective_from}T00:00:00`).toLocaleDateString('pt-BR')} · {Object.keys(currentConfig.payload.groups).length} grupos cadastrados.</p></div>
              {draftForCurrent && <span className="status-chip warning">{draftForCurrent.approval_status.replaceAll('_', ' ')}</span>}
            </div>
            <div className="due-date-admin-table">
              {Object.entries(currentConfig.payload.groups).sort(([a], [b]) => Number(a) - Number(b)).map(([group, days]) => (
                <div className="due-date-admin-row" key={group}>
                  <div><span className="eyebrow">GRUPO</span><strong>{group}</strong></div>
                  <div><span className="eyebrow">DIAS PERMITIDOS</span><strong>{days.map((day) => String(day).padStart(2, '0')).join(' · ')}</strong></div>
                  <span />
                </div>
              ))}
            </div>
          </section>

          {configs.filter((row) => row.id > 0).length > 1 && <section className="due-date-admin-history"><h3>Histórico de vigências</h3>{configs.filter((row) => row.id > 0).map((row) => <article key={row.id}><div><strong>{row.title}</strong><small>Vigência {new Date(`${row.effective_from}T00:00:00`).toLocaleDateString('pt-BR')}</small></div><button className="text-button" onClick={() => openConfig(row)} disabled={!canEdit}>Editar</button></article>)}</section>}
        </>
      )}

      {formOpen && canEdit && (
        <section className="editor-form-card">
          <div className="pending-head"><div><p className="eyebrow">EDIÇÃO CONTROLADA</p><h2>Grupos e vencimentos permitidos</h2></div><button className="text-button" onClick={() => setFormOpen(false)}>Fechar</button></div>
          <form onSubmit={save}>
            <div className="config-meta-grid">
              <label>Vigência<input type="date" value={effectiveFrom} onChange={(event) => setEffectiveFrom(event.target.value)} required /></label>
              <label>Identificação<input value={title} onChange={(event) => setTitle(event.target.value)} required /></label>
            </div>
            <div className="notice info">Serviço restrito ao titular. Essa condição permanece fixa na ferramenta operacional.</div>
            <div className="due-date-admin-table">
              {groupRows.map((row) => (
                <div className="due-date-admin-row" key={row.id}>
                  <label><span>Grupo</span><input inputMode="numeric" value={row.group} onChange={(event) => updateRow(row.id, { group: event.target.value.replace(/\D/g, '') })} placeholder="60" required /></label>
                  <label><span>Dias permitidos</span><input value={row.daysText} onChange={(event) => updateRow(row.id, { daysText: event.target.value })} placeholder="7, 10, 12, 15, 17, 20" required /></label>
                  <button type="button" className="danger-link" onClick={() => removeRow(row.id)} disabled={groupRows.length <= 1}>Remover</button>
                </div>
              ))}
            </div>
            <div className="tool-actions"><button type="button" className="secondary-button compact" onClick={addGroup}>+ Adicionar grupo</button><button type="submit" className="primary-button compact" disabled={saving}>{saving ? 'Salvando...' : 'Salvar no rascunho'}</button></div>
          </form>
        </section>
      )}
    </section>
  )
}
